import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Button, Box, IconButton, Stack, Skeleton, TextField, Tooltip } from '@mui/material';
import { BookmarkAdd as BookmarkAddIcon } from '@mui/icons-material';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import StyledSnackbar from '../Components/CustomUIElements/StyledSnackbar';
import { classes } from './styles';
import Api from '../Job/API';
import { IJob, IJobData, IPersonOption } from './types';
import CircularProgress from '@mui/material/CircularProgress';
import {
  changeURL,
  editDescriptionDefault,
  editGeneralJobDefault,
  editGoogleScriptDefault,
  editJobObject,
  editPanelWorkflowDefault,
  editRevolveDefault,
  editSourcesDefault,
  editTemplateDefault
} from '../Jobs/config';
import WarningDialog from '../Components/Modals/WarningDialog';
import UpdateIcon from '@mui/icons-material/Update';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userTimeZone } from '../utils/formatToUserTimeZone';
import EditJobTabs from './EditJobTabs';
import NewJobTemplateModal from '../Templates/JobTemplate/Modals/NewJobTemplateModal';
import EditJobTabContent from './EditJobTabContent';
import { useFeatureFlag } from '../../FeatureFlags';
import { formatUserPermissions } from '../Components/Utilities/formatUserPermissions';

const URL_PARAMS = new URL(window.location.href);
const BASE_URL = window.location.origin;

export default function EditJob({
  jobId,
  jobFeedActive,
  canAddIndeedDemographicQ
}: {
  jobId: number;
  jobFeedActive: boolean;
  canAddIndeedDemographicQ: boolean;
}) {
  const getEditTabNumber = (urlParams: string) => changeURL(0, false, urlParams);
  const [job, setJob] = useState<IJob | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [editTabValue, setEditTabValue] = useState<number>(
    getEditTabNumber(URL_PARAMS.search) as number
  );
  const [copied, setCopied] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [changeTab, setChangeTab] = useState<[boolean, null | number]>([false, null]);
  const [generalTabJobData, setGeneralTabJobData] = useState<IJobData | null>(null);
  const [panelWorkflowTabJobData, setPanelWorkflowTabJobData] = useState<{
    panel_chairs: {
      error: string;
      required: boolean;
      value: IPersonOption[];
    };
    panel_members: {
      error: string;
      required: boolean;
      value: IPersonOption[];
    };
    panel_observers: {
      error: string;
      required: boolean;
      value: IPersonOption[];
    };
  } | null>(null);
  const [googleScriptTabJobData, setGoogleScriptTabJobData] = useState<{
    google_conversion_script: {
      error: string;
      required: boolean;
      value: string;
    };
  } | null>(null);
  const [revolveTabJobData, setRevolveTabJobData] = useState<{
    revolve_position_title: {
      error: string;
      required: boolean;
      value: string;
    };
  } | null>(null);
  const [descriptionTabJobData, setDescriptionTabJobData] = useState<{
    summary: { error: string; required: boolean; value: string };
    description: { error: string; required: boolean; value: string };
  }>({
    summary: { error: '', required: false, value: '' },
    description: { error: '', required: false, value: '' }
  });
  const [templateTabJobData, setTemplateTabJobData] = useState<{
    template_enabled: boolean;
    template: { error: string; required: boolean; value: string };
  }>({
    template_enabled: false,
    template: { error: '', required: false, value: '' }
  });
  const [sourcesTabJobData, setSourcesTabJobData] = useState<{
    sources: { error: string; required: boolean; value: IJob['sources'] };
  } | null>(null);
  const [sourceListIds, setSourceListIds] = useState<number[]>([]);
  const [candidateAttachments, setCandidateAttachments] = useState<
    { label: string; required: boolean; value: string }[]
  >([]);
  const [warningModalOpen, setWarningModalOpen] = useState(editTabValue === 4);
  const [hasActiveSeekAd, setHasActiveSeekAd] = useState(false);
  const [isToggled, setIsToggled] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    state: 'success' | 'warning' | 'error';
  }>({
    message: '',
    state: 'success'
  });
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [newJobTemplateModalOpen, setNewJobTemplateModalOpen] = useState<boolean>(false);
  const [jobTemplateName, setJobTemplateName] = useState<string>('');
  const jobTemplateEnabled = useFeatureFlag('jobTemplateEnabled');
  const userPermissions = formatUserPermissions();
  const queryClient = useQueryClient();

  const handleEditTabChange = (event: React.SyntheticEvent, newValue: number) => {
    URL_PARAMS.search = String(changeURL(newValue, true, '') || '');
    window.history.pushState(null, '', URL_PARAMS.href);
    if (editTabValue === 1) {
      newValue === 4 && setWarningModalOpen(true);
      return setChangeTab([true, newValue]);
    }
    if (newValue === 4) {
      setWarningModalOpen(true);
    }
    setEditTabValue(newValue);
  };

  useQuery({
    queryKey: ['job', jobId],
    queryFn: async () => {
      const jobTemplatePage = URL_PARAMS.href.includes('job_templates');
      if (jobTemplatePage) {
        return await Api.getJobTemplate(jobId);
      }
      return await Api.getJob(jobId);
    },
    onSuccess: (data) => {
      setFetchedData(data);
    },
    onError: (error) => {
      setSnackbar({
        state: 'error',
        message: `There was an error getting job, ${error}`
      });
    }
  });

  const setFetchedData = (data) => {
    setJobLoading(true);
    const jobResponse = data;
    setJob(jobResponse);
    setGeneralTabJobData(editGeneralJobDefault(jobResponse));
    setPanelWorkflowTabJobData(editPanelWorkflowDefault(jobResponse));
    setGoogleScriptTabJobData(editGoogleScriptDefault(jobResponse));
    setRevolveTabJobData(editRevolveDefault(jobResponse));
    setDescriptionTabJobData(editDescriptionDefault(jobResponse));
    setTemplateTabJobData(editTemplateDefault(jobResponse));
    setSourcesTabJobData(editSourcesDefault(jobResponse));
    setSourceListIds(jobResponse.sources.map((source) => source.id));
    setCandidateAttachments(
      jobResponse.candidate_attachments.map((attachment) => ({
        ...attachment,
        id: attachment.value
      }))
    );
    setJobLoading(false);
    setLastUpdate(jobResponse.updated_at);
    setJobTemplateName(jobResponse.job_template_name);
  };

  const fetchActiveSeekAds = useCallback(async () => {
    try {
      const response = await Api.getActiveSeekAd(jobId);
      if (response.job_ads.active && response.job_ads.active.length > 0) {
        setHasActiveSeekAd(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [jobId]);

  useEffect(() => {
    if (job && job.state !== 'template') {
      fetchActiveSeekAds();
    }
  }, [fetchActiveSeekAds, job]);

  useEffect(() => {
    window.onbeforeunload = function () {
      if (isEdited) {
        return true;
      } else {
        return;
      }
    };
    return () => {
      window.onbeforeunload = null;
    };
  }, [isEdited]);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const updateData = async ({ id, data }) =>
    job?.state === 'template'
      ? Api.updateJobTemplate(id, { job: { ...data, job_template_name: jobTemplateName } })
      : Api.updateJob(id, { job: data });

  const mutation = useMutation(updateData, {
    onSuccess: (data) => {
      setSnackbar({
        message: data?.errors ? data.errors : 'Updated job successfully',
        state: data?.errors ? 'error' : 'success'
      });
      setIsEdited(false);
      queryClient.invalidateQueries(['job', jobId]);
    },
    onError: (error) => {
      setSnackbar({
        message: error?.errors || 'Something has gone wrong with saving your job',
        state: 'error'
      });
    }
  });

  const handleUpdateJob = async () => {
    setIsUpdating(true);
    const editObj = editJobObject(
      candidateAttachments,
      generalTabJobData,
      descriptionTabJobData,
      templateTabJobData,
      revolveTabJobData,
      googleScriptTabJobData,
      panelWorkflowTabJobData,
      sourceListIds
    );
    mutation.mutate({ id: jobId, data: editObj });

    setIsUpdating(false);
    setIsEdited(false);
  };

  const lastSaved = new Date(lastUpdate);
  const lastSavedFormat = (job: IJob | null) => {
    const timeZoneString = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const pickedtimezone = job?.time_zone ?? userTimeZone ?? timeZoneString;
    const timeZone = moment.tz(pickedtimezone).format('z');
    const timeString = lastSaved.toLocaleString('en-US', {
      timeZone: pickedtimezone,
      hour: 'numeric',
      minute: 'numeric',
      year: '2-digit',
      month: '2-digit',
      day: '2-digit'
    });
    return `${timeString} ${timeZone}`;
  };

  const canOpenNewJobTemplateModal =
    jobTemplateEnabled &&
    userPermissions?.Entities?.['Create / Edit / Delete Job Templates'] &&
    job &&
    job.state !== 'template' &&
    job.state !== 'draft';

  return (
    <Box sx={classes.jobPageContainer}>
      <Box sx={classes.editJobPageHeader}>
        {jobLoading ? (
          <Skeleton animation="wave" width={600} height={56} />
        ) : (
          <>
            {job?.state === 'template' ? (
              <TextField
                label="Job template name"
                required
                sx={classes.jobTemplateName}
                value={jobTemplateName}
                onChange={(e) => setJobTemplateName(e.target.value)}
              />
            ) : (
              <Box sx={{ color: '#666666' }}>
                <h4 id="reference">{job?.reference}</h4>
                <h1 id="title">{job?.title}</h1>
              </Box>
            )}
          </>
        )}
        {canOpenNewJobTemplateModal && (
          <Tooltip placement="top" title="Save job as template" arrow>
            <BookmarkAddIcon
              sx={{ color: '#666666', cursor: 'pointer' }}
              fontSize="large"
              onClick={() => setNewJobTemplateModalOpen(true)}
            />
          </Tooltip>
        )}
      </Box>
      {job?.state !== 'template' && (
        <Box sx={classes.linkContainer}>
          {jobLoading ? (
            <Skeleton animation="wave" width={400} height={50} />
          ) : (
            job?.public_url && (
              <>
                <a href={job?.public_url} target="_blank" rel="noopener noreferrer">
                  <Box id="external-link" sx={classes.link}>
                    {job.public_url}
                  </Box>
                </a>
                <CopyToClipboard text={job?.public_url} onCopy={handleCopy}>
                  <Button sx={classes.copyButton} id="copy-link">
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </CopyToClipboard>
              </>
            )
          )}
        </Box>
      )}
      {jobLoading ? (
        <Skeleton animation="wave" sx={{ marginBottom: '20px' }} width={200} height={56} />
      ) : (
        <Box sx={classes.backAndLastSaveContainer}>
          <Stack
            flexDirection="row"
            alignItems="center"
            onClick={() => {
              if (job?.state === 'template') {
                window.location.href = `${BASE_URL}/admin/templates?tab=jobTemplate`;
              } else {
                window.location.href = `${BASE_URL}${job?.job_link}`;
              }
            }}
            sx={{ ...classes.backToJobButton, marginBottom: 'unset' }}
          >
            <IconButton sx={classes.iconGrey}>
              <ArrowCircleLeftIcon />
            </IconButton>
            <Box>Back</Box>
          </Stack>
          <Stack sx={classes.lastSaved}>
            {isUpdating ? (
              <>
                <CircularProgress size={12} color="inherit" sx={{ marginRight: 1 }} />
                Saving
              </>
            ) : (
              <>
                <UpdateIcon sx={{ margin: '4px', fontSize: '1rem' }} />
                Last saved {lastSavedFormat(job)}
              </>
            )}
          </Stack>
        </Box>
      )}
      <Box sx={classes.editTabsContainer}>
        <EditJobTabs
          editTabValue={editTabValue}
          handleEditTabChange={handleEditTabChange}
          isToggled={isToggled}
          setIsToggled={setIsToggled}
          job={job}
        />
        {jobLoading ? (
          <Skeleton animation="wave" sx={{ marginLeft: '2rem' }} width={200} height={56} />
        ) : (
          <EditJobTabContent
            editTabValue={editTabValue}
            setEditTabValue={setEditTabValue}
            changeTab={changeTab}
            setChangeTab={setChangeTab}
            job={job}
            jobId={jobId}
            generalTabJobData={generalTabJobData}
            setGeneralTabJobData={setGeneralTabJobData}
            descriptionTabJobData={descriptionTabJobData}
            setDescriptionTabJobData={setDescriptionTabJobData}
            templateTabJobData={templateTabJobData}
            setTemplateTabJobData={setTemplateTabJobData}
            sourcesTabJobData={sourcesTabJobData}
            setSourcesTabJobData={setSourcesTabJobData}
            sourceListIds={sourceListIds}
            setSourceListIds={setSourceListIds}
            candidateAttachments={candidateAttachments}
            setCandidateAttachments={setCandidateAttachments}
            jobFeedActive={jobFeedActive}
            handleUpdateJob={handleUpdateJob}
            setIsEdited={setIsEdited}
            isUpdating={isUpdating}
            lastSavedFormat={lastSavedFormat}
            panelWorkflowTabJobData={panelWorkflowTabJobData}
            setPanelWorkflowTabJobData={setPanelWorkflowTabJobData}
            canAddIndeedDemographicQ={canAddIndeedDemographicQ}
            jobLoading={jobLoading}
            setSnackbar={setSnackbar}
            handleEditTabChange={handleEditTabChange}
            revolveTabJobData={revolveTabJobData}
            setRevolveTabJobData={setRevolveTabJobData}
            googleScriptTabJobData={googleScriptTabJobData}
            setGoogleScriptTabJobData={setGoogleScriptTabJobData}
          />
        )}
      </Box>
      <StyledSnackbar
        message={snackbar.message}
        state={snackbar.state}
        setSnackbarState={setSnackbar}
      />
      <WarningDialog
        isDialogOpen={hasActiveSeekAd && warningModalOpen}
        setDialogOpen={setWarningModalOpen}
        title={'Warning'}
        description={
          '<strong>Any changes</strong> (adding, editing, removing etc.) to your questions will affect and block the importing of applicants from SEEK into :Recruit.'
        }
        buttonText="I understand"
      />
      {canOpenNewJobTemplateModal && (
        <NewJobTemplateModal
          isOpen={newJobTemplateModalOpen}
          handleClose={() => setNewJobTemplateModalOpen(false)}
          jobId={jobId}
          setSnackbar={setSnackbar}
        />
      )}
    </Box>
  );
}
